<!--
  The diff reader's minimap: the open file in miniature down its right
  edge, in place of its scrollbar, to stay oriented by (logic/minimap).
  On a canvas, the shape of the code as the reader shows it: each line's
  words as bars, indented as it is, in its syntax's colors (faint where
  unchanged, plain over the diff's tints where removed, added or moved),
  on the base and head halves of a split diff; each change a solid region,
  warm until reviewed and calmer after, its head a rule; unchanged heads
  and folded lines as faint and dotted bands. Over it: the unreviewed edge
  at the left, ticks at the right for threads, drafts, comments on
  selected code, check annotations and the comment being written; the
  line cursor; and the window that is what the reader shows. A press
  scrolls the reader to that place, and dragging keeps it following; the
  wheel and the keys scroll the reader as ever.

  Thin (`thin`, the reader's z m or the switch above the strip): the same
  places without the code's shape, for the reader's width: changes and
  folds as bands, each changed line a mark in its kind's lane, and the
  edge, ticks, cursor and window as ever.

  It is measured from the rendered diff (so folds, reviewed stubs and open
  threads count at the height they take), again on the next frame whenever
  the reader's content changes; a line's words are read from its cell once
  and kept until the cell is rendered anew. The cursor or a selection
  moving measures only the cursor. Colors are read from the reader's
  tokens, again when the theme changes. The strip is aria-hidden: the same
  places are reached by keys (n/p, ]t, …).
-->
<script lang="ts">
  import { onDestroy } from 'svelte';
  import {
    minimapMarks,
    paintStyle,
    runsOf,
    scrollFor,
    shape,
    thinShape,
    toneOf,
    viewportBox,
    type LineKind,
    type Mark,
    type Measured,
    type Paint,
    type Rect,
    type Run,
    type Segment,
    type ShapeBand,
    type ShapeCell,
    type ShapeRow,
  } from '../logic/minimap';

  interface Props {
    /** The diff reader: what scrolls, and whose content is measured. */
    pane: HTMLElement;
    /** Threads whose first comment is on selected code (SpanNotes): marked as such. */
    spanIds?: ReadonlySet<string>;
    /** The thin strip of the changes, instead of the picture of the file. */
    thin?: boolean;
    /** Switches between the two (the reader's own command, so its key and its announcement go with it). */
    ontoggle?: () => void;
    /** The key that switches them, as a tooltip's ` (z m)`, or nothing. */
    hint?: string;
  }
  let { pane, spanIds = new Set(), thin = false, ontoggle, hint = '' }: Props = $props();

  let strip: HTMLDivElement | undefined = $state();
  let canvas: HTMLCanvasElement | undefined = $state();
  let marks = $state<Mark[]>([]);
  let view = $state({ top: 0, height: 0 });

  /** The line a thing in an inline row is under: the row before it, its last line number. */
  function lineOf(el: Element): string | null {
    const row = el.closest('.inline')?.previousElementSibling;
    const nums = row ? [...row.querySelectorAll('.gutter')].map((g) => g.textContent?.trim()).filter(Boolean) : [];
    return nums.at(-1) ?? null;
  }
  const at = (el: Element) => {
    const n = lineOf(el);
    return n ? ` · line ${n}` : '';
  };

  /** Where the reader's content starts, in the page: what a measured top is taken from. */
  const origin = () => pane.getBoundingClientRect().top - pane.scrollTop;
  function measureEach(els: Iterable<Element>, kind: Measured['kind'], title: (el: Element) => string, base: number, out: Measured[]) {
    for (const el of els) {
      const r = el.getBoundingClientRect();
      if (r.height || r.width) out.push({ kind, top: r.top - base, height: r.height, title: title(el) });
    }
  }
  /** The cursor, measured in the reader's content. */
  function measureCursor(base: number): Measured[] {
    const out: Measured[] = [];
    measureEach(pane.querySelectorAll('.line-cursor'), 'cursor', () => 'Line cursor', base, out);
    return out;
  }
  /** Everything marked over the code, measured in the reader's content. */
  function measure(base: number): Measured[] {
    const out: Measured[] = [];
    // A change's own top, not its head's: the head sticks to the reader's top as it scrolls.
    measureEach(pane.querySelectorAll('.diff-block.change:not(.reviewed)'), 'unreviewed', (el) => `Unreviewed ${el.querySelector('.block-type')?.textContent?.trim().toLowerCase() ?? 'change'}`, base, out);
    for (const el of pane.querySelectorAll('.thread')) {
      const id = el.getAttribute('data-thread');
      const kind = id && spanIds.has(id) ? 'span' : el.classList.contains('draft') ? 'draft' : el.classList.contains('resolved') ? 'resolved' : 'thread';
      const title = { span: 'Comment on selected code', draft: 'Your draft', resolved: 'Resolved thread', thread: 'Thread' }[kind];
      measureEach([el], kind, () => `${title}${at(el)}`, base, out);
    }
    measureEach(pane.querySelectorAll('.annotation'), 'annotation', (el) => `Check annotation: ${el.querySelector('.head b')?.textContent?.trim() ?? ''}${at(el)}`, base, out);
    measureEach(pane.querySelectorAll('.composer'), 'composer', (el) => `Comment being written${at(el)}`, base, out);
    out.push(...measureCursor(base));
    return out;
  }

  /** A cell's text as the highlighter colored it: each text node, with the tone of its nearest syn-* element. */
  function segmentsOf(code: Element): Segment[] {
    const out: Segment[] = [];
    const walk = (node: Node, tone: Segment['tone']) => {
      for (const child of node.childNodes) {
        if (child.nodeType === Node.TEXT_NODE) out.push({ text: child.textContent ?? '', tone });
        else if (child instanceof Element) {
          const cls = child.getAttribute('class') ?? '';
          walk(child, cls.includes('syn-') ? toneOf(cls) : tone);
        }
      }
    };
    walk(code, 'text');
    return out;
  }
  /** A cell's words, read once: kept until the cell's code is rendered anew (its first node replaced). */
  const read = new WeakMap<Element, { first: Node | null; runs: Run[] }>();
  function runsFor(code: Element): Run[] {
    const kept = read.get(code);
    if (kept && kept.first === code.firstChild) return kept.runs;
    const runs = runsOf(segmentsOf(code));
    read.set(code, { first: code.firstChild, runs });
    return runs;
  }
  const kindOf = (cell: Element): LineKind =>
    cell.classList.contains('moved') ? 'moved' : cell.classList.contains('removed') ? 'removed' : cell.classList.contains('added') ? 'added' : 'context';

  /** The code's rows and the diff's bands, measured in the reader's content: one box read for each. */
  function measureShape(base: number): { rows: ShapeRow[]; bands: ShapeBand[] } {
    const rows: ShapeRow[] = [];
    for (const row of pane.querySelectorAll('.split-row, .unified-row')) {
      const r = row.getBoundingClientRect();
      if (!r.height) continue;
      const split = row.classList.contains('split-row');
      const cells: ShapeCell[] = [];
      [...row.children].forEach((cell, i) => {
        const code = cell.classList.contains('source-cell') && !cell.classList.contains('vacant') ? cell.querySelector('code') : null;
        if (code) cells.push({ half: split ? (i ? 1 : 0) : null, kind: kindOf(cell), runs: runsFor(code) });
      });
      rows.push({ top: r.top - base, height: r.height, cells });
    }
    const bands: ShapeBand[] = [];
    for (const block of pane.querySelectorAll('.diff-block')) {
      const r = block.getBoundingClientRect();
      if (!r.height) continue;
      const head = block.querySelector(':scope > .block-head')?.getBoundingClientRect().height ?? 0;
      if (block.classList.contains('change')) {
        bands.push({ kind: block.classList.contains('reviewed') ? 'reviewed' : 'change', top: r.top - base, height: r.height });
        bands.push({ kind: 'head', top: r.top - base, height: head });
      } else bands.push({ kind: 'context', top: r.top - base, height: head });
    }
    for (const el of pane.querySelectorAll('.scope-stub, .reviewed-stub:not(.open)')) {
      const r = el.getBoundingClientRect();
      if (r.height) bands.push({ kind: 'folded', top: r.top - base, height: r.height });
    }
    return { rows, bands };
  }

  /** Paints the shape: each paint's color read from the reader's tokens once, its rectangles together. */
  function paint(rects: readonly Rect[]) {
    if (!canvas || !strip) return;
    const dpr = window.devicePixelRatio || 1;
    const w = strip.clientWidth;
    const h = strip.clientHeight;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    const style = getComputedStyle(pane);
    const groups = new Map<Paint, Rect[]>();
    for (const r of rects) {
      const g = groups.get(r.paint);
      if (g) g.push(r);
      else groups.set(r.paint, [r]);
    }
    for (const [p, list] of groups) {
      const { color, alpha } = paintStyle(p);
      ctx.fillStyle = style.getPropertyValue(color).trim() || 'transparent';
      ctx.globalAlpha = alpha;
      // Folded lines dotted, so they read as lines left out, not as code.
      if (p === 'band:folded') for (const r of list) for (let x = r.x; x < r.x + r.w; x += 3) ctx.fillRect(x, r.y, 1.5, r.h);
      else for (const r of list) ctx.fillRect(r.x, r.y, r.w, r.h);
    }
    ctx.globalAlpha = 1;
  }

  /** What the next frame measures: only the cursor, or everything. */
  let pending: 'cursor' | 'all' | null = null;
  let frame = 0;
  /** Measures again on the next frame (one measure however many changes come before it). */
  function schedule(what: 'cursor' | 'all' = 'all') {
    pending = pending === 'all' ? 'all' : what;
    if (!frame) frame = requestAnimationFrame(() => {
      frame = 0;
      const all = pending === 'all';
      pending = null;
      redraw(all);
    });
  }
  function redraw(all: boolean) {
    if (!strip) return;
    const h = strip.clientHeight;
    const base = origin();
    if (all) {
      marks = minimapMarks(measure(base), pane.scrollHeight, h);
      const { rows, bands } = measureShape(base);
      paint((thin ? thinShape : shape)(rows, bands, pane.scrollHeight, h, window.devicePixelRatio || 1));
    } else marks = [...marks.filter((m) => m.kind !== 'cursor'), ...minimapMarks(measureCursor(base), pane.scrollHeight, h)];
    view = viewportBox(pane.scrollTop, pane.clientHeight, pane.scrollHeight, h);
  }
  function onscroll() {
    if (strip) view = viewportBox(pane.scrollTop, pane.clientHeight, pane.scrollHeight, strip.clientHeight);
  }

  /** Classes a cell gains and loses that change nothing drawn but the cursor. */
  const PASSING = new Set(['line-cursor', 'selected', 'covered']);
  /** Whether a change in the reader is only the cursor or a selection moving over its cells. */
  function onlyCursor(records: MutationRecord[]): boolean {
    return records.every((r) => {
      if (r.type !== 'attributes' || r.attributeName !== 'class' || !(r.target instanceof Element) || !r.target.classList.contains('source-cell')) return false;
      const was = new Set((r.oldValue ?? '').split(/\s+/).filter(Boolean));
      const now = new Set(r.target.classList);
      for (const c of was) if (!now.has(c) && !PASSING.has(c)) return false;
      for (const c of now) if (!was.has(c) && !PASSING.has(c)) return false;
      return true;
    });
  }

  $effect(() => {
    void spanIds;
    void thin;
    const content = new MutationObserver((records) => schedule(onlyCursor(records) ? 'cursor' : 'all'));
    content.observe(pane, { subtree: true, childList: true, characterData: true, attributes: true, attributeOldValue: true, attributeFilter: ['class', 'open', 'hidden'] });
    // The theme switch (tokens.css's data-theme) and the system's preference change the tokens painted in.
    const theme = new MutationObserver(() => schedule());
    theme.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class', 'style'] });
    const scheme = window.matchMedia?.('(prefers-color-scheme: dark)');
    const rescheme = () => schedule();
    scheme?.addEventListener?.('change', rescheme);
    const sized = new ResizeObserver(() => schedule());
    sized.observe(pane);
    if (strip) sized.observe(strip);
    pane.addEventListener('scroll', onscroll, { passive: true });
    schedule();
    return () => {
      content.disconnect();
      theme.disconnect();
      scheme?.removeEventListener?.('change', rescheme);
      sized.disconnect();
      pane.removeEventListener('scroll', onscroll);
    };
  });
  onDestroy(() => cancelAnimationFrame(frame));

  /** Scrolls the reader to the place under `y` on the strip. */
  function go(e: PointerEvent) {
    if (!strip) return;
    const y = e.clientY - strip.getBoundingClientRect().top;
    pane.scrollTop = scrollFor(y, strip.clientHeight, pane.clientHeight, pane.scrollHeight);
    onscroll();
  }
  function down(e: PointerEvent) {
    if (e.button !== 0) return;
    // Not a focus target: the reader keeps the focus, and its keys.
    e.preventDefault();
    strip?.setPointerCapture?.(e.pointerId);
    go(e);
  }
  function move(e: PointerEvent) {
    if (strip?.hasPointerCapture?.(e.pointerId)) go(e);
  }
</script>

{#if ontoggle}
  <!-- Outside the aria-hidden strip, so it can be reached; not a focus stop on press, so the reader keeps its keys. -->
  <button
    type="button"
    class="minimap-toggle"
    class:thin
    aria-pressed={thin}
    aria-label={thin ? 'Widen the minimap to the picture of the file' : 'Narrow the minimap to a thin strip of its changes'}
    title="{thin ? 'Picture of the file' : 'Thin minimap'}{hint}"
    onpointerdown={(e) => e.preventDefault()}
    onclick={ontoggle}
  >{thin ? '‹' : '›'}</button>
{/if}
<!-- svelte-ignore a11y_no_static_element_interactions (aria-hidden: the keys reach the same places) -->
<div class="minimap" class:thin class:toggled={!!ontoggle} bind:this={strip} aria-hidden="true" onpointerdown={down} onpointermove={move}>
  <canvas bind:this={canvas}></canvas>
  {#each marks as m, i (i)}
    <div class="mark {m.kind}" style:top="{m.top}px" style:height="{m.height}px" title={m.title}></div>
  {/each}
  <div class="view" style:top="{view.top}px" style:height="{view.height}px"></div>
</div>

<style>
  /* STRIP_PX (logic/minimap) wide, THIN_PX thin; diff.css keeps that room beside the reader. Below its switch, when it has one. */
  .minimap { position: absolute; top: 0; right: 0; bottom: 0; width: 60px; background: var(--surface); border-left: 1px solid var(--line); cursor: pointer; touch-action: none; user-select: none; z-index: 3; }
  .minimap.thin { width: 16px; }
  .minimap.toggled { top: 16px; }
  .minimap-toggle { position: absolute; top: 0; right: 0; width: 60px; height: 16px; padding: 0; display: grid; place-items: center; font: 600 12px/1 var(--mono, monospace); color: var(--muted); background: var(--surface); border: 0; border-left: 1px solid var(--line); border-bottom: 1px solid var(--line); cursor: pointer; z-index: 4; }
  .minimap-toggle.thin { width: 16px; }
  .minimap-toggle:hover { color: var(--fg); background: color-mix(in srgb, var(--fg) 8%, var(--surface)); }
  .minimap-toggle:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
  canvas { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; }
  .mark { position: absolute; pointer-events: auto; }
  /* The review edge at the left, beside CODE_X's start. */
  .unreviewed { left: 1px; width: 3px; border-radius: 1px; background: var(--warn); }
  /* Notes as ticks at the right edge, past CODE_X's end: there to find, not pills over the code. */
  .thread, .resolved, .draft, .span, .annotation, .composer { right: 1px; width: 6px; min-height: 2px; border-radius: 1px; }
  .thread { background: var(--syn-call); }
  .resolved { background: var(--muted); opacity: .6; }
  .draft { background: transparent; box-shadow: inset 0 0 0 1px var(--syn-call); }
  .span { background: var(--syn-call); box-shadow: inset 2px 0 var(--fg); }
  .annotation { background: var(--bad); }
  .composer { background: var(--fg); }
  .thin .thread, .thin .resolved, .thin .draft, .thin .span, .thin .annotation, .thin .composer { width: 3px; }
  .cursor { left: 0; right: 0; height: 2px !important; background: var(--fg); opacity: .8; }
  /* What the reader shows: a window over the picture, as an editor's minimap slider, the code still read through it. */
  .view { position: absolute; left: 0; right: 0; min-height: 8px; background: color-mix(in srgb, var(--fg) 13%, transparent); border-block: 1px solid color-mix(in srgb, var(--fg) 45%, transparent); pointer-events: none; }
  .minimap:hover .view { background: color-mix(in srgb, var(--fg) 18%, transparent); }
</style>
